/**
 * Owner-facing full-database export.
 *
 * The owner asks for a copy of their own business's database; a platform administrator
 * approves, which opens a 48-hour window. The approval is scoped to the owner — a reseller
 * cannot spend it, and the owner cannot spend a reseller's — so what this page shows is
 * always about this owner's own request.
 *
 * Only a business on its own dedicated database has anything to export. One still sharing
 * a schema with other businesses gets an explanation rather than a disabled button: there
 * is no separate database yet, and saying so is more useful than greying out a control.
 */
import { useState } from 'react';
import { Database as DatabaseIcon } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { PageHeader } from '../../components/PageHeader';
import { useActiveBusiness } from '../../api/hooks';
import { useOwnDbExport, requestOwnDbExport, downloadOwnDatabase } from '../../api/resources';
import { formatDate } from '../../lib/format';

export function Database() {
  const { data: business, isLoading: bizLoading } = useActiveBusiness();
  const businessId = business?.id ?? '';
  const dedicated = business?.dbMode === 'managed';
  const { data, refetch, isLoading } = useOwnDbExport(businessId, dedicated);

  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const current = data?.current ?? null;

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await requestOwnDbExport(businessId, reason); setReason(''); await refetch(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'The request could not be sent.'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setDownloading(true); setErr(null);
    try { await downloadOwnDatabase(businessId, current?.schemaName ?? 'database'); await refetch(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'The download failed.'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Database"
        subtitle="Download a complete backup of your business's own database."
      />

      {bizLoading && <Card className="p-5"><p className="text-sm text-zinc-500 dark:text-zinc-400">Loading…</p></Card>}

      {!bizLoading && !dedicated && (
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            Your business currently runs on a shared database, which it holds together with other
            businesses — so there is no separate database of your own to download yet. Ask us about
            moving onto a dedicated database and this becomes available.
          </p>
        </Card>
      )}

      {!bizLoading && dedicated && (
        <Card className="p-5 border-zinc-200 dark:border-zinc-700">
          {err && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{err}</p>}
          {isLoading && <p className="text-sm text-zinc-500 dark:text-zinc-400">Loading…</p>}

          {!isLoading && !current && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                You can request a full copy of your database as a single compressed file. Because it
                contains everything your business holds — every sale, every customer's details and your
                staff's login records — each request is reviewed by an administrator before the download
                is opened.
              </p>
              <label className="block text-sm">
                <span className="text-zinc-700 dark:text-zinc-200">Why do you need it?</span>
                <textarea
                  className="mt-1 w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-900 p-2 text-sm"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. we want our own offline backup kept at the shop"
                />
              </label>
              <Button
                size="sm"
                disabled={busy || reason.trim().length < 3}
                iconLeft={<DatabaseIcon className="w-4 h-4" />}
                onClick={submit}
              >
                {busy ? 'Sending…' : 'Request a copy'}
              </Button>
            </div>
          )}

          {!isLoading && current?.state === 'requested' && (
            <div className="space-y-2">
              <Badge tone="warning">Waiting for approval</Badge>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Your request has been sent. Nothing is downloadable until an administrator approves it,
                and you will be emailed either way. Your business carries on exactly as normal meanwhile.
              </p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">You wrote: “{current.reason}”</p>
            </div>
          )}

          {!isLoading && current?.state === 'approved' && (
            <div className="space-y-3">
              <Badge tone="success">Approved</Badge>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Your download is open until{' '}
                {current.expiresAt ? formatDate(current.expiresAt) : 'the window closes'}. You can
                download more than once in that time; after it closes you will need to ask again.
              </p>
              <Button
                size="sm"
                disabled={downloading}
                iconLeft={<DatabaseIcon className="w-4 h-4" />}
                onClick={download}
              >
                {downloading ? 'Preparing…' : 'Download .sql.gz'}
              </Button>
              {downloading && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Your database is being packaged now — a busy shop takes around 20 seconds. The file
                  saves when it is complete.
                </p>
              )}
            </div>
          )}

          {!isLoading && (data?.history?.length ?? 0) > 0 && (
            <div className="mt-5 pt-4 border-t border-zinc-200 dark:border-zinc-700">
              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">Past requests</p>
              <ul className="mt-2 space-y-1.5 text-sm text-zinc-600 dark:text-zinc-300">
                {(data?.history ?? []).map((r) => (
                  <li key={r.publicId} className="flex flex-wrap items-center gap-2">
                    <Badge tone={r.state === 'approved' ? 'success' : r.state === 'requested' ? 'warning' : 'neutral'}>
                      {r.state}
                    </Badge>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">{formatDate(r.createdAt)}</span>
                    {/* A reseller may also have asked for this database; say which is which rather
                        than showing the owner a request they did not make with no explanation. */}
                    {r.requestedByKind === 'reseller' && (
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">(asked by your provider)</span>
                    )}
                    <span className="truncate">“{r.reason}”</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
